// A unit revoking its own key (Quick Actions' Unregister Device, DLG-112; coordinator's design 2026-10-18). Before a
// register forgets its identity it asks the cloud to stop trusting the key, so a forgotten unit's key no longer signs in
// or syncs and Commander stops showing it as active. The call is a wire route on the device's own session:
// - signed by the key being revoked: a DPoP proof over the request with the device token bound to that key;
// - it names nothing, so it can revoke only the key that signed it; the record and its other units stay;
// - idempotent: the same key asking again (an answer lost on the way) is told it is already revoked, by its proof alone;
// - throttled per key, and audited as `fleet.revokeDeviceKey` by the device, through the same command every node sees.
// A register that is offline or refused forgets itself anyway, and support clears the key from Commander.
import { z } from 'zod';
import { DeviceId, IsoInstant } from '../../primitives/codecs.ts';

/** The route (cloud only: revocation comes from the authority that issues device tokens). */
export const DEVICE_FORGET_PATH = '/v1/auth/devices/forget' as const;

export const DEVICE_FORGET = {
  /**
   * The reason `fleet.revokeDeviceKey` records for this route's revocations: a stable code, not prose, so a repeat knows
   * the revocation was this route's (review of #1469). Commander shows it as "Unregistered on the unit".
   */
  reasonCode: 'unit_unregistered',
  /** Calls one key may make in a window, then it waits out the lock (429). */
  maxCallsPerKey: 10,
  windowMinutes: 10,
} as const;

/** The answer (in the identity routes' envelope, `data`). */
export const DeviceForgetAnswer = z.strictObject({
  deviceId: DeviceId,
  keyId: z.uuid(),
  revokedAt: IsoInstant,
  /** True when this key had already revoked itself (a repeat): nothing was written. */
  alreadyRevoked: z.boolean(),
}).meta({ id: 'DeviceForgetAnswer' });
export type DeviceForgetAnswer = z.output<typeof DeviceForgetAnswer>;
